import type { OfflineDb } from "./db";
import type { Order, OrderDetail } from "@/lib/types/orders";

/**
 * **Referenz und Wörterbuch sind Typparameter**, keine Importe: dieser Speicher
 * reicht beide nur durch und liest nie hinein. Sie aus `@/features/…` bzw.
 * `@/lib/api/…` zu holen, zöge die halbe Weboberfläche in `shared/mobil/` —
 * die Bindung in `src/features/mobile/offline/dayStore.ts` setzt sie ein.
 *
 * Der Tagesbestand auf dem Gerät (S4.3) — „Laden beim Tagesstart" aus
 * SESSIONS.md.
 *
 * Abgelegt wird, was die Fahreransicht zum Arbeiten braucht: die Tagesliste,
 * jeder Auftrag darin als vollständiges Detail (Checkliste, Ressourcen, bereits
 * vorhandener Nachweis) und die Nachschlagedaten, ohne die aus Adressen und
 * Auftragsarten bloße uuids würden.
 *
 * **Der Bestand ist eine Kopie, kein zweiter Wahrheitsort.** Er wird bei jedem
 * erfolgreichen Laden vollständig ersetzt und nie fortgeschrieben; was der Fahrer
 * ändert, steht in der Warteschlange (outbox.ts) und wird beim Anzeigen darüber
 * gelegt (merge.ts). Deshalb kann hier auch nichts verloren gehen, wenn das
 * Aufräumen zu großzügig ist.
 *
 * **Nicht abgelegt: die Bytes bereits übertragener Fotos.** Ihre `downloadUrl`
 * zeigt nach `/api`, und der Service Worker lässt `/api` bewusst unberührt (kein
 * Kundendatum im Browser-Cache, src/pwa/service-worker.js). Ein Foto von gestern
 * bleibt offline also ein leeres Bild; die eigenen, noch nicht übertragenen
 * kommen aus der Warteschlange und sind da.
 */

export interface CachedDay {
  date: string;
  orders: Order[];
  savedAt: string;
}

const REFERENCE_KEY = "aktuell";

/**
 * Das Wörterbuch (S5.6) liegt im **selben Lager** wie die Stammdaten, unter
 * einem zweiten Schlüssel.
 *
 * Ein fünftes Lager hätte `DB_VERSION` erhöht, und ein Versionswechsel ist auf
 * einem Gerät im Funkloch der ungünstigste Zeitpunkt für einen `onblocked`
 * (offline/db.ts). Der Grund ist aber nicht nur Vorsicht: das Wörterbuch *ist*
 * Nachschlagedatenbestand — es hat dieselbe Lebensdauer wie Adressen und
 * Auftragsarten, wird wie sie vollständig ersetzt und geht beim Abmelden mit
 * ihnen weg.
 */
const TEXTS_KEY = "texte";

export interface DayStore<Referenz, Texte> {
  readDay(date: string): Promise<CachedDay | null>;
  /** Ersetzt den Tag und räumt alles weg, was weder zu ihm noch zu `keepOrderIds` gehört. */
  saveDay(date: string, orders: Order[], keepOrderIds?: string[]): Promise<void>;
  readOrder(orderId: string): Promise<OrderDetail | null>;
  saveOrder(order: OrderDetail): Promise<void>;
  readReference(): Promise<Referenz | null>;
  saveReference(snapshot: Referenz): Promise<void>;
  /** Das Wörterbuch des Tages (S5.6) — Original → Übersetzung, für die Anzeige in der Sprache des Fahrers. */
  readTexte(): Promise<Texte | null>;
  saveTexte(texte: Texte): Promise<void>;
  /** Wirft den gesamten Bestand weg — beim Abmelden. Die Warteschlange bleibt (siehe unten). */
  verwirfBestand(): Promise<void>;
}

export function createDayStore<Referenz, Texte>(db: OfflineDb): DayStore<Referenz, Texte> {
  return {
    readDay: (date) => db.get<CachedDay>("day", date),

    async saveDay(date, orders, keepOrderIds = []) {
      await db.put("day", date, { date, orders, savedAt: new Date().toISOString() } satisfies CachedDay);

      // Aufräumen gehört an dieselbe Stelle wie das Schreiben: sonst wächst der
      // Speicher mit jedem Arbeitstag, und niemand merkt es, weil nichts kaputt
      // geht. Ein Auftrag, auf den noch ein Eintrag der Warteschlange zeigt,
      // bleibt — sonst verlöre die Ansicht ihn genau in dem Moment, in dem sie
      // ihn noch anzeigen soll.
      const keep = new Set([...orders.map((order) => order.id), ...keepOrderIds]);
      for (const key of await db.keys("day")) if (key !== date) await db.remove("day", key);
      for (const key of await db.keys("orders")) if (!keep.has(key)) await db.remove("orders", key);
    },

    readOrder: (orderId) => db.get<OrderDetail>("orders", orderId),

    saveOrder: (order) => db.put("orders", order.id, order),

    readReference: () => db.get<Referenz>("reference", REFERENCE_KEY),

    saveReference: (snapshot) => db.put("reference", REFERENCE_KEY, snapshot),

    readTexte: () => db.get<Texte>("reference", TEXTS_KEY),

    saveTexte: (texte) => db.put("reference", TEXTS_KEY, texte),

    /**
     * Beim Abmelden (`useMemberLogout`). Seit der Sitzungshinweis einen
     * Kaltstart ohne Netz überlebt, überlebt sonst auch der Tagesbestand die
     * Abmeldung — und der nächste Mensch am selben Gerät sähe die Aufträge des
     * vorigen, sobald sein erster Netzaufruf scheitert. Der Bestand hängt am
     * Tag und nicht am Mitglied; ihn zu behalten wäre nur zu verantworten, wenn
     * er das täte.
     *
     * **Die Warteschlange bleibt.** Darin liegt Arbeit, die noch niemand
     * bekommen hat — sie zu löschen hieße, dem Fahrer sein Häkchen und sein Foto
     * wegzunehmen, weil er sich abgemeldet hat. Dass sie damit einen
     * Nutzerwechsel übersteht, ist eine offene Frage und steht als solche in
     * STATUS.md.
     */
    async verwirfBestand() {
      for (const store of ["day", "orders", "reference"] as const) {
        for (const key of await db.keys(store)) await db.remove(store, key);
      }
    }
  };
}

/** Der eine Tagesbestand der Anwendung. */
